import { Component, EventEmitter, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { AuthService } from '../../../services/auth.service';

function matchPasswords(group: AbstractControl): ValidationErrors | null {
  const next = group.get('newPassword')?.value;
  const confirm = group.get('confirmNewPassword')?.value;
  return next && confirm && next !== confirm ? { mismatch: true } : null;
}

// Misma regla que el servidor: letras y números.
function lettersAndNumbers(control: AbstractControl): ValidationErrors | null {
  const v = String(control.value ?? '');
  return /[A-Za-z]/.test(v) && /\d/.test(v) ? null : { lettersAndNumbers: true };
}

/**
 * Se muestra cuando un superAdmin restableció la contraseña del usuario (temporal): permite
 * cambiarla de inmediato sin pasar por el perfil.
 */
@Component({
  selector: 'app-password-prompt-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <div class="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
      <div class="relative w-full max-w-md max-h-[92vh] overflow-y-auto bg-[#0F172A] border border-[#1E2E5D] rounded-3xl p-6 shadow-2xl shadow-emerald-500/10 text-white">
        <div class="text-center mb-4">
          <div class="text-4xl mb-2">🔑</div>
          <h3 class="text-lg font-black tracking-tight text-white mb-1.5">Actualiza tu contraseña</h3>
          <p class="text-xs text-slate-300 leading-relaxed px-2">
            Un administrador restableció tu contraseña con una temporal. Cámbiala ahora por una que solo tú conozcas.
          </p>
        </div>

        <ng-container *ngIf="!done; else successTpl">
          <form [formGroup]="form" (ngSubmit)="submit()" class="space-y-3" novalidate>
            <label class="block">
              <span class="text-xs text-slate-300">Contraseña temporal</span>
              <input
                type="password"
                formControlName="currentPassword"
                autocomplete="current-password"
                class="mt-1 w-full rounded-xl bg-slate-900/60 border border-slate-700/60 px-3 py-2 text-sm text-white outline-none focus:border-emerald-400"
              />
            </label>
            <label class="block">
              <span class="text-xs text-slate-300">Nueva contraseña</span>
              <input
                type="password"
                formControlName="newPassword"
                autocomplete="new-password"
                class="mt-1 w-full rounded-xl bg-slate-900/60 border border-slate-700/60 px-3 py-2 text-sm text-white outline-none focus:border-emerald-400"
              />
              <span class="block text-[11px] mt-1" [ngClass]="newPasswordInvalid ? 'text-amber-300' : 'text-slate-400'">
                Mínimo 8 caracteres, con letras y números.
              </span>
            </label>
            <label class="block">
              <span class="text-xs text-slate-300">Repite la nueva contraseña</span>
              <input
                type="password"
                formControlName="confirmNewPassword"
                autocomplete="new-password"
                class="mt-1 w-full rounded-xl bg-slate-900/60 border border-slate-700/60 px-3 py-2 text-sm text-white outline-none focus:border-emerald-400"
              />
              <span *ngIf="form.hasError('mismatch') && form.get('confirmNewPassword')?.dirty" class="block text-[11px] mt-1 text-amber-300">
                Las contraseñas no coinciden.
              </span>
            </label>

            <p *ngIf="error" class="text-xs text-red-400">{{ error }}</p>

            <div class="flex flex-col-reverse sm:flex-row gap-2 pt-2">
              <button
                type="button"
                (click)="dismiss()"
                [disabled]="working"
                class="flex-1 rounded-xl border border-slate-600 px-4 py-2.5 text-sm font-semibold text-slate-200 hover:bg-slate-800 transition-colors"
              >
                Más tarde
              </button>
              <button
                type="submit"
                [disabled]="form.invalid || working"
                class="flex-1 rounded-xl bg-gradient-to-r from-emerald-500 to-[#00F0FF] px-4 py-2.5 text-sm font-bold text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ working ? 'Guardando…' : 'Cambiar contraseña' }}
              </button>
            </div>
          </form>
        </ng-container>

        <ng-template #successTpl>
          <p class="text-center text-sm text-emerald-400 font-semibold py-4">✅ Contraseña actualizada correctamente.</p>
        </ng-template>
      </div>
    </div>
  `
})
export class PasswordPromptModalComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);

  @Output() closeModal = new EventEmitter<void>();

  working = false;
  done = false;
  error: string | null = null;

  readonly form = this.fb.group(
    {
      currentPassword: this.fb.control('', [Validators.required]),
      newPassword: this.fb.control('', [Validators.required, Validators.minLength(8), lettersAndNumbers]),
      confirmNewPassword: this.fb.control('', [Validators.required])
    },
    { validators: matchPasswords }
  );

  get newPasswordInvalid(): boolean {
    const c = this.form.get('newPassword');
    return !!c && c.dirty && c.invalid;
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.working) return;
    this.working = true;
    this.error = null;
    const { currentPassword, newPassword } = this.form.getRawValue();
    try {
      await this.authService.changePassword(currentPassword as string, newPassword as string);
      this.done = true;
      setTimeout(() => this.closeModal.emit(), 1500);
    } catch (e) {
      this.error = (e as Error)?.message || 'No pudimos cambiar la contraseña. Inténtalo de nuevo.';
    } finally {
      this.working = false;
    }
  }

  dismiss(): void {
    this.closeModal.emit();
  }
}
